import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { homesPaymentStatus } from "@/lib/api";

export default function SubscribeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const s = await homesPaymentStatus(sessionId);
        if (s.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(s.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center" data-testid="subscribe-success">
      {state === "polling" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-[#2563EB]" />
          <h1 className="mt-4 text-2xl font-black text-[#0F172A]">Confirming your subscription…</h1>
          <p className="mt-1 text-slate-500">This only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-500" data-testid="subscribe-paid" />
          <h1 className="mt-4 text-3xl font-black text-[#0F172A]">You're subscribed!</h1>
          <p className="mt-1 max-w-md text-slate-500">Your plan is active. Set up your profile and publish your hosted microsite.</p>
          <Link to="/homes/agent-portal" className="mt-6 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="go-to-portal">Go to Agent Portal</Link>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <XCircle className="h-14 w-14 text-slate-400" />
          <h1 className="mt-4 text-2xl font-black text-[#0F172A]">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="mt-1 max-w-md text-slate-500">{state === "timeout" ? "Your payment may still be completing. Check the Agent Portal in a minute." : "We couldn't confirm this payment."}</p>
          <div className="mt-6 flex gap-3">
            <Link to="/homes/agent-portal" className="rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white hover:bg-[#1D4ED8]">Agent Portal</Link>
            <Link to="/homes/subscribe" className="rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-[#0F172A] hover:bg-slate-50">View plans</Link>
          </div>
        </>
      )}
    </div>
  );
}
